import { sha256 } from '@noble/hashes/sha2.js';
import { fromBase64, fromUtf8, toBase64, toHex, utf8 } from './bytes';
import { open, seal } from './crypto';
import type { CloudBlobEntry, CloudManifest } from './manifest';

/**
 * What one push changed in the manifest, sent beside the whole sealed body so a
 * device holding the manifest one version back can come forward by reading the
 * change instead of the whole vault's list. Ported from
 * `shared/.../vault/cloud/CloudManifestRecord.kt`; the JSON keys, the AAD and
 * the state digest are wire format, pinned by `vault-wire-vectors.json`.
 *
 * `state` is the resulting manifest's digest: a reader that applies the record
 * and does not arrive at it goes back to the snapshot. A push with no record
 * is a gap, which sends readers to the snapshot too.
 */
export interface CloudManifestRecord {
  v: number;
  /** path → blobId, for every path the push added or pointed elsewhere. */
  set?: Record<string, string>;
  rm?: string[];
  /** blobId → entry, for every blob entry the push added or changed. */
  blobs?: Record<string, CloudBlobEntry>;
  blobsRm?: string[];
  state: string;
}

/** The backend's cap on a sealed record, under Firestore's document limit. */
export const MAX_SEALED_RECORD_CHARS = 700_000;

const STATE_BYTES = 32;

/**
 * The XOR of the SHA-256 of every entry, each file line and each blob line
 * hashed on its own — a digest a change can move without reading the rest.
 * Remembered per manifest object, so a run of writes from one tab hashes the
 * vault once and moves the digest by each write after that.
 */
const knownStates = new WeakMap<CloudManifest, string>();

export function manifestState(manifest: CloudManifest): string {
  const known = knownStates.get(manifest);
  if (known) return known;
  const acc = new Uint8Array(STATE_BYTES);
  for (const [path, blobId] of Object.entries(manifest.files)) fold(acc, fileEntry(path, blobId));
  for (const [id, entry] of Object.entries(manifest.blobs)) fold(acc, blobEntry(id, entry));
  const state = toHex(acc);
  knownStates.set(manifest, state);
  return state;
}

/** What changed from `before` to `after`, which is `version`. */
export function recordBetween(before: CloudManifest, after: CloudManifest, version: number): CloudManifestRecord {
  const set: Record<string, string> = {};
  for (const [path, blobId] of Object.entries(after.files)) {
    if (before.files[path] !== blobId) set[path] = blobId;
  }
  const rm = Object.keys(before.files).filter((path) => !(path in after.files));
  const blobs: Record<string, CloudBlobEntry> = {};
  for (const [id, entry] of Object.entries(after.blobs)) {
    if (!sameEntry(before.blobs[id], entry)) blobs[id] = entry;
  }
  const blobsRm = Object.keys(before.blobs).filter((id) => !(id in after.blobs));
  const record: CloudManifestRecord = { v: version, state: '' };
  if (Object.keys(set).length > 0) record.set = set;
  if (rm.length > 0) record.rm = rm;
  if (Object.keys(blobs).length > 0) record.blobs = blobs;
  if (blobsRm.length > 0) record.blobsRm = blobsRm;
  record.state = stateAfter(record, before, manifestState(before));
  knownStates.set(after, record.state);
  return record;
}

/** `before`'s state moved by exactly the entries `record` changes. */
export function stateAfter(record: CloudManifestRecord, before: CloudManifest, stateBefore: string): string {
  const acc = Uint8Array.from(fromHexState(stateBefore));
  const set = record.set ?? {};
  const blobs = record.blobs ?? {};
  for (const path of new Set([...(record.rm ?? []), ...Object.keys(set)])) {
    const blobId = before.files[path];
    if (blobId !== undefined) fold(acc, fileEntry(path, blobId));
  }
  for (const [path, blobId] of Object.entries(set)) fold(acc, fileEntry(path, blobId));
  for (const id of new Set([...(record.blobsRm ?? []), ...Object.keys(blobs)])) {
    const entry = before.blobs[id];
    if (entry !== undefined) fold(acc, blobEntry(id, entry));
  }
  for (const [id, entry] of Object.entries(blobs)) fold(acc, blobEntry(id, entry));
  return toHex(acc);
}

/** One version's sealed record, as the backend serves it. */
export interface SealedRecord {
  version: number;
  sealed: string;
}

/**
 * `manifest`, the vault at `from`, brought to `upTo` by `records` — the chain
 * after `from` — each checked against the state it states as it applies. Null
 * when the chain does not reach, or a record does not open or drifts.
 */
export function bringForward(
  dataKey: Uint8Array,
  manifest: CloudManifest,
  from: number,
  upTo: number,
  records: SealedRecord[],
): CloudManifest | null {
  let state = manifestState(manifest);
  const at: CloudManifest = { blobs: { ...manifest.blobs }, files: { ...manifest.files } };
  let version = from;
  for (const next of records) {
    // One past `upTo` is a push that landed after the envelope was read.
    if (next.version !== version + 1 || next.version > upTo) break;
    const record = openRecord(dataKey, next.version, next.sealed);
    if (!record) return null;
    const arrived = stateAfter(record, at, state);
    if (arrived !== record.state) return null;
    for (const path of record.rm ?? []) delete at.files[path];
    Object.assign(at.files, record.set ?? {});
    for (const id of record.blobsRm ?? []) delete at.blobs[id];
    Object.assign(at.blobs, record.blobs ?? {});
    state = arrived;
    version = next.version;
  }
  if (version < upTo) return null;
  knownStates.set(at, state);
  return at;
}

/** Bound to its version, so a record cannot be served as another version's. */
export function recordAad(version: number): Uint8Array {
  return utf8(`pondering.manifest-record.v1:${version}`);
}

/** The sealed record, base64, or null when it is too big to send as one. */
export function sealRecordOrNull(dataKey: Uint8Array, record: CloudManifestRecord): string | null {
  const sealed = toBase64(seal(dataKey, recordAad(record.v), utf8(JSON.stringify(record))));
  return sealed.length <= MAX_SEALED_RECORD_CHARS ? sealed : null;
}

/** Null when `sealed` does not open as `version`'s record under `dataKey`. */
export function openRecord(dataKey: Uint8Array, version: number, sealed: string): CloudManifestRecord | null {
  let bytes: Uint8Array;
  try {
    bytes = fromBase64(sealed);
  } catch {
    return null;
  }
  const plain = open(dataKey, recordAad(version), bytes);
  if (!plain) return null;
  try {
    const record = JSON.parse(fromUtf8(plain)) as CloudManifestRecord;
    return record.v === version ? record : null;
  } catch {
    return null;
  }
}

function fileEntry(path: string, blobId: string): Uint8Array {
  return utf8(`f\u0000${path}\u0000${blobId}`);
}

function blobEntry(id: string, entry: CloudBlobEntry): Uint8Array {
  return utf8(`b\u0000${id}\u0000${entry.version}\u0000${entry.contentHash}\u0000${entry.sizeEnc}`);
}

function fold(acc: Uint8Array, entry: Uint8Array): void {
  const h = sha256(entry);
  for (let i = 0; i < STATE_BYTES; i++) acc[i]! ^= h[i]!;
}

function fromHexState(hex: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/.test(hex)) throw new Error(`a manifest state is ${STATE_BYTES} bytes of hex`);
  const out = new Uint8Array(STATE_BYTES);
  for (let i = 0; i < STATE_BYTES; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function sameEntry(a: CloudBlobEntry | undefined, b: CloudBlobEntry): boolean {
  return a !== undefined && a.version === b.version && a.contentHash === b.contentHash && a.sizeEnc === b.sizeEnc;
}
